import { useCallback, useEffect, useState } from 'react';

import { CircularProgress, Typography } from '@mui/material';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { blockingStepId } from 'features/onboarding/lib/checklist';
import { useOnboardingChecklist } from 'features/onboarding/lib/useOnboardingChecklist';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { ChecklistStepRow } from 'features/onboarding/ui/ChecklistStepRow';
import type { PaneSlot } from 'features/onboarding/ui/ControlCenterPane';
import { EMPTY_PANE, PaneList, PaneStrip } from 'features/onboarding/ui/ControlCenterPane';

// What the accordion is showing. `null` follows the current step as the checklist advances; a step
// id pins that step open; COLLAPSED is the user having closed the one that was open.
const COLLAPSED = Symbol('collapsed');
type Expansion = string | typeof COLLAPSED | null;

/** The setup checklist, as the AI assistant tab's pane: the current step pinned above the chat. */
export function useChecklistPane(): PaneSlot {
  const checklist = useOnboardingChecklist();
  const isExpanded = useOnboardingDialogStore((state) => state.expanded.assistant);
  const setExpanded = useOnboardingDialogStore((state) => state.setExpanded);
  const [expansion, setExpansion] = useState<Expansion>(null);
  // Whether there was still setup to do when the panel opened. Finishing the last step leaves the
  // checklist on screen — the user has earned seeing it complete — but the next open is just a chat.
  const [wasUnfinishedOnOpen, setWasUnfinishedOnOpen] = useState<boolean | null>(null);
  // Someone part-way through would otherwise open on a list scrolled to the top, with the step they
  // are on below the fold. A ref callback fires exactly when that row mounts, which is the moment
  // there is something to scroll to; an effect had to guess at it from state that is already settled
  // by then, so it ran while the list was still behind the loading branch and scrolled nothing.
  const focusCurrentRow = useCallback((row: HTMLLIElement | null) => {
    row?.scrollIntoView({ block: 'center' });
  }, []);

  const hasCurrentStep = checklist.status === 'ready' && checklist.current !== null;
  const isChecklistSettled = checklist.status === 'ready';
  useEffect(() => {
    if (isChecklistSettled) {
      setWasUnfinishedOnOpen((known) => known ?? hasCurrentStep);
    }
  }, [isChecklistSettled, hasCurrentStep]);

  // Wherever the user left it. Until they first expand or collapse it the catalog decides: `narrative`
  // opens collapsed to the strip, `full` opens expanded.
  const displayMode = checklist.status === 'ready' ? checklist.checklist.display_mode : null;
  const listOpen = isExpanded ?? displayMode === 'full';

  if (checklist.status === 'unavailable' || wasUnfinishedOnOpen === false) {
    return EMPTY_PANE;
  }

  if (checklist.status === 'loading' || wasUnfinishedOnOpen === null) {
    return {
      ...EMPTY_PANE,
      strip: (
        <div className="flex flex-none items-center gap-2 border-grey30 border-b px-6 py-3 max-md:px-4">
          <CircularProgress size={16} />
          <Typography variant="body2" className="text-grey60">
            Loading your setup steps…
          </Typography>
        </div>
      ),
    };
  }

  const { sections, total_count } = checklist.checklist;
  const { steps, current } = checklist;
  const expandedStepId = expansion === COLLAPSED ? null : (expansion ?? current?.step_id ?? null);

  const settle = (step: ChecklistStep, status: StepStatus) =>
    checklist.settle(step, status).then((changed) => {
      if (!changed) {
        return;
      }
      // Ticking a step off moves the user on, so the expansion follows to whatever is current now.
      // Undo is the opposite: they're going back to this step, so it stays open under them.
      setExpansion(status === 'pending' ? step.step_id : null);
    });

  return {
    isListOpen: listOpen,
    onAsk: () => setExpanded('assistant', false),
    strip: (
      <PaneStrip isListOpen={listOpen} onToggle={() => setExpanded('assistant', !listOpen)}>
        {current && (
          <Typography variant="body2" className="font-medium text-grey60 text-xs">
            {`Step ${steps.indexOf(current) + 1} of ${total_count}`}
          </Typography>
        )}
        <Typography className="truncate font-medium text-grey90 text-sm">
          {current?.title ?? 'Setup complete'}
        </Typography>
      </PaneStrip>
    ),
    list: (
      <PaneList isOpen={listOpen} label="Setup steps">
        <div className="flex flex-col gap-4">
          {sections.map((section) => (
            <div key={section.section_id} className="flex flex-col gap-2">
              {/* A single section reads as "the checklist"; more than one (dashboard plus an
                  integration) needs its headings to stay meaningful. */}
              {sections.length > 1 && (
                <Typography variant="body2" className="font-semibold text-grey70">
                  {section.section_label}
                </Typography>
              )}
              <ul className="flex list-none flex-col gap-2 p-0">
                {section.steps.map((step) => (
                  <ChecklistStepRow
                    key={step.step_id}
                    ref={step.step_id === current?.step_id ? focusCurrentRow : undefined}
                    step={step}
                    blockerTitle={checklist.blockerTitle(step)}
                    isExpanded={expandedStepId === step.step_id}
                    onExpand={() => setExpansion(expandedStepId === step.step_id ? COLLAPSED : step.step_id)}
                    onOpenBlocker={() => setExpansion(blockingStepId(step))}
                    onSettle={(status) => settle(step, status)}
                    busy={checklist.pendingAction(step.step_id)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </PaneList>
    ),
  };
}
